import AppInfoFooter from "@/components/AppInfoFooter"
import Button from "@/components/Button"
import InputErrorMessage from "@/components/InputErrorMessage"
import { TextInput } from "@/components/InputFields"
import InputLabel from "@/components/InputLabel"
import Link from "@/components/Link"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import { useLogInWithMfaCodeMutation } from "@/data/queries"
import { useAuthTokensAtom } from "@/data/store"
import { BackendErrorCode, backendErrorCodeFor } from "@/model/BackendErrors"
import { MfaChallenge } from "@/model/LoginResponse"
import { ROUTES } from "@/routes"
import { verificationCodeSchema } from "@/schemas/zod-schemas"
import cn from "@/utils/cn"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { Navigate, useLocation, useNavigate } from "react-router"

export type VerifyCodePageStateParams = {
  challenge: MfaChallenge
  redirectTo: string
  email?: string
}

type VerifyCodeFormData = {
  code: string
}

const sessionExpiredNotice =
  "Your verification code has expired. Please sign in again."

const codeSentSentenceFor = ({ method, codeDestination }: MfaChallenge) => {
  switch (method) {
    case "SMS_MFA":
      return `We sent a code by text message to ${codeDestination ?? "your phone"}.`
    case "EMAIL_OTP":
      return `We sent a code by email to ${codeDestination ?? "your email address"}.`
  }
}

type SignInAgainLinkProps = {
  email?: string
  children: string
}

const SignInAgainLink = ({ email, children }: SignInAgainLinkProps) => (
  <Link
    to={ROUTES.LOGIN}
    replace={true}
    state={{ email }}
    className="underline"
  >
    {children}
  </Link>
)

type VerifyCodeErrorMessageProps = {
  errorCode: BackendErrorCode
  email?: string
}

const VerifyCodeErrorMessage = ({
  errorCode,
  email,
}: VerifyCodeErrorMessageProps) => {
  switch (errorCode) {
    case "INVALID_CODE":
      return "The code is incorrect. Please check it and try again."
    case "LOGIN_FAILED":
      return (
        <>
          We could not verify your code. Please&nbsp;
          <SignInAgainLink email={email}>sign in again</SignInAgainLink>.
        </>
      )
    default:
      return (
        <>
          Something went wrong. Please&nbsp;
          <SignInAgainLink email={email}>sign in again</SignInAgainLink>.
        </>
      )
  }
}

const VerifyCodePage = () => {
  const locationState = useLocation().state as VerifyCodePageStateParams | null
  const [authTokens] = useAuthTokensAtom()
  const navigate = useNavigate()
  const {
    mutate: logInWithMfaCode,
    isPending,
    error: logInError,
  } = useLogInWithMfaCodeMutation()

  const { register, handleSubmit, formState } = useForm<VerifyCodeFormData>({
    mode: "all",
    defaultValues: { code: "" },
    resolver: zodResolver(verificationCodeSchema),
  })

  if (!locationState) {
    return <Navigate to={ROUTES.LOGIN} replace={true} />
  }
  const { challenge, redirectTo, email } = locationState

  const isLoggedIn = authTokens !== null
  const logInErrorCode = backendErrorCodeFor(logInError)
  const { errors } = formState

  const onSubmit = ({ code }: VerifyCodeFormData) =>
    logInWithMfaCode(
      { challenge, code },
      {
        onSuccess: () => navigate(redirectTo, { replace: true }),
        onError: (error) => {
          if (backendErrorCodeFor(error) === "SESSION_EXPIRED") {
            navigate(ROUTES.LOGIN, {
              replace: true,
              state: { email, notice: sessionExpiredNotice },
            })
          }
        },
      }
    )

  return (
    <>
      {isLoggedIn && <Navigate to={redirectTo} replace={true} />}
      <Page>
        <div className="flex h-full flex-col justify-between">
          <div
            className={cn(
              "mx-auto mt-10 w-full max-w-[460px] rounded-xl bg-white px-10 py-12 shadow-2xl"
            )}
          >
            <h2 className="mb-4 text-center text-3xl font-bold text-primary/90">
              Enter your verification code
            </h2>
            <h3 className="mb-6 text-pretty text-center text-lg text-primary/90">
              {codeSentSentenceFor(challenge)}
            </h3>
            <form
              className="mt-8 flex flex-col gap-5"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="flex flex-col gap-1">
                <InputLabel htmlFor="code" className="ml-1">
                  Verification code
                </InputLabel>
                <TextInput
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  disabled={isPending}
                  {...register("code")}
                  placeholder="6-digit code"
                  isError={!!errors?.code?.message}
                  className="text-center text-2xl tracking-widest"
                />
                <InputErrorMessage
                  message={errors?.code?.message}
                  className="ml-1"
                />
                <p className="ml-1 text-sm text-primary/90">
                  The code is valid for a few minutes. Didn&apos;t get it?&nbsp;
                  <SignInAgainLink email={email}>
                    Sign in again
                  </SignInAgainLink>{" "}
                  to request a new one.
                </p>
              </div>

              <div className="ml-1 flex">
                <Link
                  to={ROUTES.LOGIN}
                  replace={true}
                  state={{ email }}
                  className={cn(
                    "inline-flex items-center hover:no-underline",
                    isPending ? "pointer-events-none" : ""
                  )}
                >
                  <span className="material-icons">chevron_left</span>Back to
                  Login
                </Link>
              </div>

              {logInErrorCode && logInErrorCode !== "SESSION_EXPIRED" && (
                <div className="my-4 flex w-full items-center rounded-md border border-red-200 bg-red-100 p-4">
                  <span className="material-icons me-4 text-red-600">
                    error_outline
                  </span>
                  <p className="text-red-600">
                    <VerifyCodeErrorMessage
                      errorCode={logInErrorCode}
                      email={email}
                    />
                  </p>
                </div>
              )}
              <Button
                variant="primary"
                type="submit"
                disabled={isPending}
                className="flex w-full gap-3 py-2 text-base"
              >
                {isPending && <Spinner />}
                Verify
              </Button>
            </form>
          </div>
          <AppInfoFooter className="mt-auto p-4" />
        </div>
      </Page>
    </>
  )
}

export default VerifyCodePage
